import React from 'react'
import cn from 'classnames'
import { Nameplate, Image, Carousel } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { track, segment } from '@mc/track-event'
import type { InstructorBioResult } from '../../types'
import style from './InstructorBio.module.scss'

interface InstructorBioProps {
  nameplate: string
  instructorBio: InstructorBioResult
  campSlug: string
  instructorTitle?: string
  withBorder?: boolean
}

export const InstructorBio = (props: InstructorBioProps) => (
  <>
    <div className='d-block d-sm-none'>
      <InstructorBioResponsive
        ltSM
        gteMD={false}
        scrollAndShowCount={1}
        {...props}
      />
    </div>
    <div className='d-none d-sm-block d-md-none'>
      <InstructorBioResponsive
        ltSM={false}
        gteMD={false}
        scrollAndShowCount={3}
        {...props}
      />
    </div>
    <div className='d-none d-md-block'>
      <InstructorBioResponsive
        ltSM={false}
        gteMD
        scrollAndShowCount={3}
        {...props}
      />
    </div>
  </>
)

type Responsive = {
  ltSM: boolean
  gteMD: boolean
  scrollAndShowCount: 1 | 3
}

const InstructorBioResponsive = ({
  nameplate,
  instructorBio: { description, bio16x9, instructorPortfolioItems },
  campSlug,
  instructorTitle,
  // Responsive
  ltSM,
  gteMD,
  scrollAndShowCount,
}: InstructorBioProps & Responsive) => {
  const { t } = useTranslation('@mc/next')

  const handleRowScrolled = (oldIndex: number, newIndex: number) => {
    track(segment.ContentRowEvents.ROW_SCROLLED, {
      direction: newIndex > oldIndex ? 'right' : 'left',
      product: 'MasterCamp',
      page_source: `${campSlug}_post_pw_unenrolled`,
      auto_scroll: false,
      tile_position_index: 0,
    })
  }

  return (
    <div className='mc-text--center mc-mb-10 mc-pt-12'>
      <div className='row no-gutters justify-content-center'>
        <div className='col-12 col-lg-8'>
          <h2 className='mc-text-h1 mc-mb-9'>
            {t('sessions.instructorBio.meetInstructor')}
          </h2>
        </div>
      </div>

      <div
        className={cn(style.instructorWrapper, 'row no-gutters mc-corners--sm')}
      >
        <div className='col-md-6'>
          <Image
            className={cn('mc-corners-top-left--2', {
              'mc-corners-bottom-left--2': gteMD,
              'mc-corners-top-right--2': !gteMD,
            })}
            src={bio16x9}
            aspectRatio={ltSM ? '16x9' : '1x1'}
            alt=''
          />
        </div>

        <div
          className={cn(
            'mc-bg-light mc-pt-6 col-md-6 mc-corners-bottom-right--2',
            {
              'mc-p-10 mc-corners-top-right--2': gteMD,
              'mc-p-8 mc-corners-bottom-left--2': !gteMD,
            },
          )}
        >
          <div className='mc-my-8'>
            <Nameplate
              src={nameplate}
              className={style.nameplate}
              alt={instructorTitle}
            />
          </div>
          <p className='mc-text--left'>{description}</p>
        </div>
      </div>

      <div className='mc-mt-9 mc-theme-light'>
        <Carousel
          controls={!ltSM}
          peek={ltSM}
          color='0,0,0,0'
          className='row'
          showCount={scrollAndShowCount}
          beforeChange={handleRowScrolled}
        >
          {instructorPortfolioItems.map(({ id, imageUrl, altText }) => (
            <div className='col-auto' key={`portfolio-${id}`}>
              <Image
                src={imageUrl}
                aspectRatio='1x1'
                className='mc-corners--sm'
                alt={altText}
              />
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  )
}
